/**
 * Site CMS API — manage a tenant's website (pages + sections) and read the
 * public site. Sections are a discriminated list; the renderer + builder both
 * key off `type`.
 */
import apiClient from './index'

export type SectionType = 'hero' | 'text' | 'spaces' | 'pricing' | 'cta' | 'gallery' | 'contact' | 'html' | 'event_inquiry' | 'lead_form' | 'form' | 'availability' | 'intake' | 'testimonials' | 'faq' | 'stats' | 'video' | 'team' | 'logos'

export interface LeadField {
  key: string
  label: string
  type: 'text' | 'email' | 'tel' | 'textarea'
  required?: boolean
}

/** A gallery image with an optional link (clickable image) + CTA button. */
export interface GalleryImage {
  url: string
  link?: string          // clicking the image navigates here
  button_label?: string  // optional CTA button rendered under the image
  button_link?: string
}

export interface PricingPlan {
  name: string
  price: string
  period?: string
  features?: string[]
  cta_label?: string
  cta_link?: string
  highlight?: boolean
}

/** A section is a loosely-typed block; fields depend on `type`. */
export interface SectionStyle {
  /** '' = default page bg · 'muted' light band · 'brand' gold band · or a #hex */
  bg?: string
  /** vertical padding of the section band */
  pad?: 'sm' | 'md' | 'lg'
  align?: 'left' | 'center'
  /** content max-width */
  width?: 'normal' | 'wide' | 'full'
}

export interface Section {
  type: SectionType
  /** Hidden sections stay in the page but are not rendered on the public site. */
  hidden?: boolean
  /** Per-section design (background, spacing, alignment, width). */
  style?: SectionStyle
  // hero
  headline?: string
  subhead?: string
  image_url?: string
  cta_label?: string
  cta_link?: string
  // text / spaces / pricing / cta / contact
  heading?: string
  body?: string
  note?: string
  // spaces — pin which spaces appear (empty/unset = show all)
  space_ids?: string[]
  // pricing
  plans?: PricingPlan[]
  // gallery — items may be a plain URL string (legacy) or a rich object with an
  // optional image link and/or a CTA button underneath.
  images?: Array<string | GalleryImage>
  // cta
  button_label?: string
  button_link?: string
  // html (raw markup / embed block)
  html?: string
  // event_inquiry (look-book intake form)
  event_type?: string
  intro?: string
  ask_dates?: boolean
  ask_headcount?: boolean
  submit_label?: string
  // lead_form (generic landing-page lead capture)
  fields?: LeadField[]
  success_message?: string
  redirect_url?: string
  form_name?: string
  // form (embed a saved form from the Forms library)
  form_id?: string
  // intake (a configured intake type from the Lead hub)
  intake_type_key?: string
  // testimonials {quote,name,role} · faq {q,a} · stats {value,label}
  items?: Array<Record<string, string>>
  // video embed (YouTube / Vimeo URL)
  video_url?: string
}

export interface SitePage {
  id: string
  company_id: string
  slug: string
  title: string
  nav_label: string
  sections: Section[]
  is_published: boolean
  is_home: boolean
  show_in_nav: boolean
  sort_order: number
  seo_description?: string
  seo_image?: string
  is_landing?: boolean
  updated_at: string
}

export interface NavItem { slug: string; nav_label: string; is_home: boolean }
export interface PublicPage { id?: string; slug: string; title: string; sections: Section[]; seo_description?: string; seo_image?: string; is_home: boolean; is_landing?: boolean }
export interface PageAnalytics { views: number; submissions: number; conversion_rate: number; series: { day: string; views: number; submissions: number }[] }
export interface SiteBrand { logo_url?: string; primary?: string; accent?: string; dark?: string; tracking_enabled?: boolean }
export interface PublicSite { nav: NavItem[]; brand?: SiteBrand; page: PublicPage | null }

export const SECTION_LABELS: Record<SectionType, string> = {
  hero: 'Hero banner',
  text: 'Text block',
  spaces: 'Bookable spaces',
  pricing: 'Pricing / plans',
  cta: 'Call to action',
  gallery: 'Image gallery',
  contact: 'Contact / tour',
  html: 'Custom HTML',
  event_inquiry: 'Event inquiry form',
  lead_form: 'Lead capture form',
  form: 'Saved form',
  availability: 'Availability calendar',
  intake: 'Intake form',
  testimonials: 'Testimonials',
  faq: 'FAQ (accordion)',
  stats: 'Stats / numbers',
  video: 'Video embed',
  team: 'Team / people',
  logos: 'Logo strip',
}

// ─────────────────── public availability (embeddable calendar) ───────────────────

export interface SpaceMonthDay { open: boolean; remaining?: number; capacity?: number; slots?: number }
export interface SpaceMonth { space_id: string; unit: 'day' | 'hour'; days: Record<string, SpaceMonthDay> }
/** One month of per-day availability for a space (no auth). `isoDate` anchors the month. */
export async function getSpaceMonth(spaceId: string, isoDate: string): Promise<SpaceMonth> {
  const res = await apiClient.get(`/public/spaces/${spaceId}/month`, { params: { date: isoDate } })
  return res.data.data
}

// ─────────────────── public saved-form embed (Forms library) ───────────────────

export interface PublicFormField {
  name: string
  label: string
  type: string
  placeholder?: string
  required?: boolean
  options?: string[]
}
export interface PublicForm {
  id: string
  name: string
  description?: string
  type: string
  fields: PublicFormField[]
  settings: { submit_button_text?: string; success_message?: string; captcha_enabled?: boolean; captcha_site_key?: string }
  styling?: Record<string, unknown>
}
/** Fetch a saved form for public rendering (no auth; only 'active' forms resolve). */
export async function getPublicForm(formId: string): Promise<PublicForm> {
  const res = await apiClient.get(`/forms/public/${formId}`)
  return res.data.data
}
/** Submit a saved form (routes through the full Forms pipeline: contact/deal/autoresponder). */
export async function submitPublicForm(formId: string, fields: Record<string, unknown>, captchaToken?: string): Promise<{ success?: boolean; message?: string }> {
  const res = await apiClient.post(`/forms/public/${formId}/submit`, { fields, captcha_token: captchaToken })
  return res.data
}

// ─────────────────────────── AI page builder ───────────────────────────

export interface AiPageBrief {
  goal: string
  audience?: string
  tone?: string
  is_landing?: boolean
  include_lead_form?: boolean
}
export interface AiPageDraft {
  title: string
  slug: string
  seo_description: string
  is_landing: boolean
  sections: Section[]
}
export async function generatePageWithAI(brief: AiPageBrief): Promise<AiPageDraft> {
  const res = await apiClient.post('/site/ai/generate', brief)
  return res.data.data
}

// ─────────────────────────── admin ───────────────────────────

export async function getPages(): Promise<SitePage[]> {
  const res = await apiClient.get('/site/pages')
  return res.data.data
}
export async function getPage(id: string): Promise<SitePage> {
  const res = await apiClient.get(`/site/pages/${id}`)
  return res.data.data
}
export async function createPage(data: Partial<SitePage>): Promise<SitePage> {
  const res = await apiClient.post('/site/pages', data)
  return res.data.data
}
export async function updatePage(id: string, data: Partial<SitePage>): Promise<SitePage> {
  const res = await apiClient.put(`/site/pages/${id}`, data)
  return res.data.data
}
export async function deletePage(id: string): Promise<void> {
  await apiClient.delete(`/site/pages/${id}`)
}

// ─────────────────────────── public ───────────────────────────

export async function getPublicSite(location: string): Promise<PublicSite> {
  const res = await apiClient.get(`/public/site/${location}`)
  return res.data.data
}
export async function getPublicPage(location: string, slug: string): Promise<PublicSite> {
  const res = await apiClient.get(`/public/site/${location}/p/${slug}`)
  return res.data.data
}

/** Record a page view for conversion analytics (fire-and-forget). */
export async function recordPageView(location: string, slug: string): Promise<void> {
  try { await apiClient.post(`/public/site/${location}/view`, { slug }) } catch { /* best-effort */ }
}

/** Submit a generic landing-page lead form. */
export async function submitLead(location: string, payload: {
  fields: Record<string, string>
  source_page?: string
  source_page_title?: string
  form_name?: string
}): Promise<{ success: boolean }> {
  const res = await apiClient.post(`/public/lead/${location}`, payload)
  return res.data
}

/** Conversion analytics for a page (admin). */
export async function getPageAnalytics(pageId: string, days = 30): Promise<PageAnalytics> {
  const res = await apiClient.get(`/site/pages/${pageId}/analytics`, { params: { days } })
  return res.data.data
}

/** A blank section pre-filled with sensible defaults for its type. */
export function blankSection(type: SectionType): Section {
  switch (type) {
    case 'hero': return { type, headline: 'Your headline', subhead: 'A short supporting line.', cta_label: 'Book a tour', cta_link: '' }
    case 'text': return { type, heading: 'Section heading', body: 'Write something here.' }
    case 'spaces': return { type, heading: 'Book a space' }
    case 'pricing': return { type, heading: 'Membership', plans: [{ name: 'Day Pass', price: '$20', period: '/day', features: ['Open seating', 'Fast wifi', 'Free coffee'], cta_label: 'Get a day pass' }] }
    case 'cta': return { type, heading: 'Ready to work?', subhead: '', button_label: 'Book now', button_link: '' }
    case 'gallery': return { type, images: [] }
    case 'contact': return { type, heading: 'Come see the space', note: 'Book a tour and we’ll show you around.' }
    case 'html': return { type, html: '<div style="text-align:center;padding:2rem">\n  <!-- Paste an embed or custom HTML here -->\n</div>' }
    case 'event_inquiry': return { type, event_type: 'Event', heading: 'Host your event here', intro: 'Tell us about your event and we’ll check dates and follow up.', ask_dates: true, ask_headcount: true, submit_label: 'Send inquiry' }
    case 'lead_form': return { type, heading: 'Get in touch', intro: 'Leave your details and we’ll be in touch.', form_name: 'Landing form', submit_label: 'Submit', success_message: 'Thanks — we’ll be in touch shortly.', fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'email', label: 'Email', type: 'email', required: true },
    ] }
    case 'form': return { type, heading: '', form_id: '' }
    case 'availability': return { type, heading: 'Check availability' }
    case 'intake': return { type, heading: '', intake_type_key: '' }
    case 'testimonials': return { type, heading: 'What members say', items: [
      { quote: 'CO4 is where our team gets its best work done.', name: 'Alex Rivera', role: 'Founder, Northwind' },
    ] }
    case 'faq': return { type, heading: 'Frequently asked', items: [
      { q: 'Do you offer day passes?', a: 'Yes — grab one online any day we’re open.' },
    ] }
    case 'stats': return { type, heading: '', items: [
      { value: '120+', label: 'Members' }, { value: '6', label: 'Meeting rooms' }, { value: '24/7', label: 'Access' },
    ] }
    case 'video': return { type, heading: 'Take a look inside', video_url: '' }
    case 'team': return { type, heading: 'Meet the team', items: [
      { url: '', name: 'Name', role: 'Role' },
    ] }
    case 'logos': return { type, heading: 'Trusted by', items: [] }
  }
}
